/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { map, includes, filter } from 'lodash';
import { Form, Button, Select } from 'semantic-ui-react';

type ConnectionFieldsProps = {
    index: number,
    salons: Array<Object>,
    manufacturers: Array<Object>,
    onRemove: Function,
};

const RemoveButton = styled(Button)`
    margin-right: 0px !important;
`;

function ConnectionFields({
    index,
    salons,
    manufacturers,
    onRemove,
}: ConnectionFieldsProps) {
    const { control, watch } = useFormContext();
    const items = watch('items') || [];

    // Build salon options - exclude salons already selected in other rows
    const selectedSalons = items
        .filter((_, i) => i !== index)
        .map(item => item && item.salon)
        .filter(Boolean);

    const salonOptions = map(salons, item => ({
        text: item.name,
        value: item.id,
        description: `${item.administrator.firstName} ${item.administrator.lastName}`,
    })).filter(opt => !includes(selectedSalons, opt.value));

    // Current row's selected salon (for filtering manufacturers)
    const currentSalon = items[index] && items[index].salon;

    // Build manufacturer options - filtered by salon, exclude already-selected
    const selectedManufacturers = items
        .filter((_, i) => i !== index)
        .map(item => item && item.manufacturer)
        .filter(Boolean);

    const filterManufacturer = item => {
        if (!item.deletedAt) {
            return (
                (!includes(selectedManufacturers, item.id)) &&
                item.salon === currentSalon
            );
        }
        return item.id === (items[index] && items[index].manufacturer);
    };

    const manufacturerOptions = currentSalon
        ? map(filter(manufacturers, filterManufacturer), item => ({
              value: item.id,
              text: item.name,
          }))
        : [];

    return (
        <Form.Group>
            <Form.Field width={8}>
                <Controller
                    name={`items[${index}].salon`}
                    control={control}
                    render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                        <Select
                            {...field}
                            value={value || ''}
                            onChange={(e, data) => onChange(data.value)}
                            options={salonOptions}
                            placeholder="Salon"
                            search
                            fluid
                        />
                    )}
                />
            </Form.Field>
            <Form.Field width={8}>
                <Controller
                    name={`items[${index}].manufacturer`}
                    control={control}
                    render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                        <Select
                            {...field}
                            value={value || ''}
                            onChange={(e, data) => onChange(data.value)}
                            options={manufacturerOptions}
                            placeholder="Manufacturer"
                            disabled={!currentSalon}
                            search
                            fluid
                        />
                    )}
                />
            </Form.Field>
            <Form.Field>
                <RemoveButton onClick={onRemove} color="red" icon="trash" basic />
            </Form.Field>
        </Form.Group>
    );
}

export default ConnectionFields;
